import { useEffect, useState } from 'react'
import {
  useCurrentCanvasSide,
  useMultiColorMode,
  useWidget,
} from '../EditorDataProvider'
import { TWidgetPosition, useLogoPosition } from './useLogoPosition.hook'
import { RadioTileGroup } from '@noissue-ui-kit/oldBranding/radioTile'
import { HiMinus, HiPlus } from 'react-icons/hi'
import { HiArrowUturnLeft, HiArrowUturnRight } from 'react-icons/hi2'

const ROTATION_STEP = 15

export function LogoSettings() {
  const { canvasConfig } = useCurrentCanvasSide()
  const { widget, updateWidget } = useWidget('logo')
  const { multiColorModeIsOn } = useMultiColorMode()
  const [maxSize, setMaxSize] = useState<{ w: number; h: number }>({
    w: 100,
    h: 100,
  })
  const [selectedPosition, setSelectedPosition] = useState<TWidgetPosition>(
    widget?.config?.positionKey || 'center'
  )

  const logoPositions = useLogoPosition({
    canvasConfig,
    widgetSize: widget?.config?.size,
  })

  useEffect(() => {
    setMaxSize({
      w: canvasConfig?.width - canvasConfig?.bleedWidth * 2,
      h: canvasConfig?.height - canvasConfig?.bleedHeight * 2,
    })
  }, [canvasConfig])

  useEffect(() => {
    updateWidget({ positionKey: selectedPosition })
  }, [selectedPosition])

  useEffect(() => {
    setSelectedPosition(widget?.config?.positionKey)
  }, [widget?.config?.positionKey])

  return (
    <>
      <div className="h-full min-h-full p-1 overflow-auto scrollbar-thin">
        {widget?.config?.positionIsConfigurable && (
          <>
            <h5 className="mb-4 text-2xl font-bold text-acai select-none">
              Adjust Position
            </h5>

            <div className="grid grid-cols-3 grid-rows-3 justify-between border-grey-30 border-2 rounded-lg p-10">
              {Object.keys(logoPositions || {})?.map(
                (positionKey: TWidgetPosition) => {
                  return (
                    <div
                      key={positionKey}
                      className={`flex group items-center justify-center select-none w-10 h-10 mx-auto my-6 bg-white border-2 border-solid rounded-full cursor-pointer
                        ${
                          positionKey === selectedPosition
                            ? 'bg-acai border-acai'
                            : 'border-grey-30'
                        }`}
                      onClick={() => {
                        setSelectedPosition(positionKey)
                      }}
                    >
                      {positionKey !== selectedPosition && (
                        <div className="hidden group-hover:flex w-6 h-6 rounded-full bg-core-grey-30" />
                      )}
                    </div>
                  )
                }
              )}
            </div>
          </>
        )}

        <div className="grid w-full grid-cols-1 gap-x-3">
          <h5 className="mt-6 mb-3 text-lg font-semibold text-acai select-none">
            Scale
          </h5>
          <RadioTileGroup
            onChange={(scale) => {
              const scaleStep = scale.key === '+' ? 1 : -1
              updateWidget({
                scaleStepCount: widget.config.scaleStepCount + scaleStep,
              })
            }}
            options={[
              {
                displayLabel: <HiPlus />,
                key: '+',
                disabled:
                  !multiColorModeIsOn &&
                  (widget?.config?.size?.w >= maxSize?.w ||
                    widget?.config?.size?.h >= maxSize?.h),
              },
              {
                displayLabel: <HiMinus />,
                key: '-',
              },
            ]}
            radioGroupClassName="grid-cols-2 w-full"
            radioItemClassName="hover:bg-black-10"
          />
        </div>

        <div className="flex flex-wrap justify-between w-full">
          <h5 className="mt-6 mb-3 text-lg font-semibold text-acai select-none">
            Rotate
          </h5>

          <RadioTileGroup
            onChange={(rotationSide) => {
              const currentRotation = widget?.config?.rotation || 0
              updateWidget({
                rotation:
                  rotationSide?.key === '+'
                    ? currentRotation + ROTATION_STEP
                    : currentRotation - ROTATION_STEP,
              })
            }}
            options={[
              {
                displayLabel: <HiArrowUturnLeft className="stroke-1" />,
                key: '-',
              },
              {
                displayLabel: <HiArrowUturnRight className="stroke-1" />,
                key: '+',
              },
            ]}
            radioGroupClassName="grid-cols-2 w-full"
            radioItemClassName="hover:bg-black-10"
          />
        </div>
      </div>
    </>
  )
}
